import { logger } from "@portal/app/_common/lib/logger";
import { captureMessage } from "@sentry/nextjs";
import type { PayloadFormQuestion } from "@portal/app/_server/services/formFunnel/payloadTypes";
import { HOSTED_STEP_FALLBACKS } from "@portal/app/_server/services/formFunnel/hostedStepDefaults";

/**
 * Builds a lookup of CMS questions keyed by customerField.
 *
 * When multiple questions map to the same customerField, the FIRST entry wins.
 * This is critical because `funnelQuestions` is ordered funnel-linked questions
 * first, followed by additional customerMappedQuestions. Overwriting with the
 * later (often orphaned) entry would cause lookupAnswer to search by the wrong
 * fieldKey, breaking pre-collection deduplication.
 */
export function buildCmsQuestionMap(
  funnelQuestions: PayloadFormQuestion[] | undefined,
): Map<string, PayloadFormQuestion> {
  const map = new Map<string, PayloadFormQuestion>();

  if (funnelQuestions) {
    for (const q of funnelQuestions) {
      if (q.customerFieldMapping?.enabled && q.customerFieldMapping.customerField) {
        if (map.has(q.customerFieldMapping.customerField)) {
          logger.warn(
            `HostedResult: skipping duplicate customerField "${q.customerFieldMapping.customerField}" ` +
            `from fieldKey "${q.fieldKey}" — already mapped by "${map.get(q.customerFieldMapping.customerField)!.fieldKey}"`,
          );
          continue;
        }
        map.set(q.customerFieldMapping.customerField, q);
      }
    }
  }

  // Safety net: code-level fallbacks for critical fields not covered by CMS data
  for (const [fieldKey, fallback] of Object.entries(HOSTED_STEP_FALLBACKS)) {
    if (!map.has(fallback.customerField)) {
      captureMessage(
        `HostedResult: using fallback for ${fieldKey} (customerField: ${fallback.customerField}). CMS customerFieldMapping is missing.`,
        "warning",
      );
      map.set(fallback.customerField, {
        id: `fallback-${fieldKey}`,
        fieldKey: fallback.fieldKey,
        type: "text",
        label: fieldKey,
        customerFieldMapping: {
          enabled: true,
          customerField: fallback.customerField,
          valueTransform: fallback.valueTransform,
        },
      } as PayloadFormQuestion);
    }
  }

  return map;
}
